import {isDev, isInternal} from "./env";
import {signUpCodeName} from "../pages/partials/SignUpFields";

const userPrefix = '/user'

export enum PLAN_TAB_KEYS {
    CORPORATE = 'corporate',
    ACADEMIC = 'academic',
}

const portSuffix = isDev() ? ':3000' : '';

export const routes = {
    // START: before login routes
    login: '/login',
    invalidLink: '/invalid-link',
    guides: '/guides',
    guide: (slug: ':slug' | string) => `/guides/${slug}`,
    openPhoto51: '/open-photo51',
    loginWithRedirect: (url: string) => `/login?redirect=${url}`,
    signUp: '/sign-up',
    // Sign up page with sign up code prefilled
    signUpWithSignUpCode: (code: string) => `/sign-up?${signUpCodeName}=${code}`,
    logout: '/logout',
    loginAgain: (url: string) => `/logout?redirect=/login?redirect=${url}`,
    logoutWithRedirect: (url: string) => `/logout?redirect=${url}`,
    home: '/',
    photo51: '/photo51',
    checkoutSuccess: '/checkout-success',
    spark: '/spark',
    download: '/download-photo51',
    plans: '/plans',
    plansAcademic: `/plans?tab=${PLAN_TAB_KEYS.ACADEMIC}`,
    plansCorporate: `/plans?tab=${PLAN_TAB_KEYS.CORPORATE}`,
    faq: '/faq',
    blog: '/blog',
    aboutUs: '/about-us',
    privacy: '/privacy',
    cookiePolicy: '/cookies',
    termsOfService: '/terms-of-service',
    landing: `${userPrefix}/landing`,
    billingPortal: '/billing-portal',
    features: '/features',
    resetPassword: '/reset-password',
    completeAccount: (id: string, hash: string) => `/complete-account/${id}/${hash}`,
    shareRequestAccessLink: (id: string, hash: string) => `/access-shared-requests/${id}/${hash}`,
    completeAccountSuccess: '/complete-account-success',
    signUpSuccess: '/sign-up-success',
    forgotPassword: '/forgot-password',
    emailVerified: '/email-verified',
    groupsJoinLink: (id: string, hash: string) => `/join/groups/${id}/${hash}`,
    coursesJoinLink: (id: string, hash: string) => `/join/courses/${id}/${hash}`,
    // END: before login routes

    // Dashboard is the old landing page after login. Is replaced by a new landing
    dashboard: userPrefix,
    // START: logged in environment routes
    personalInformation: userPrefix+'/account',
    groups: userPrefix+'/groups',
    groupsCreate: userPrefix+'/groups/create',
    group: (id: string | number) => `${userPrefix}/groups/${id}`,
    subscriptions: (key = '') => userPrefix+'/subscriptions'+(key ? `/${key}` : ''),
    getIndividualSubscription: userPrefix+'/subscriptions/get-individual-subscription',
    security: userPrefix+'/security',
    reagents: userPrefix+'/reagents',
    resources: '/resources',
    resourceView: (href: string | number) => `/resources/${href}`,
    photo51Cloud: userPrefix+'/cloud-apps/photo51',
    limsCloud: `${window.location.protocol}//${window.location.hostname}${userPrefix}/cloud-apps/lims/`,
    elnCloud: `${window.location.protocol}//${window.location.hostname}${portSuffix}${userPrefix}/cloud-apps/eln`,
    elnCloudGroup: (id: string | number) => `${window.location.protocol}//${window.location.hostname}${portSuffix}${userPrefix}/cloud-apps/eln?scopeGroupId=${id}`,
    accountEdit: userPrefix+'/account/edit',
    twoFactorAuthentication: userPrefix+'/enable-two-factor-authentication',
    twoFactorAuthenticationSuccess: userPrefix+'/two-factor-authentication-success',
    twoFactorChallenge: userPrefix+'/two-factor-challenge',
    groupsEdit: (id: string) => userPrefix+'/groups/edit/'+id,
    // END: logged in environment routes

    gmail: 'https://gmail.com',
    microsoftOutlook: 'https://outlook.office.com',
}

// all the admin web routes. Not the backend API routes.
export const newString = 'new';


export const adminRoutes = {
    notifications: '/admin/notifications',
    sendNotification: '/admin/send-notifications',
    communicationMocks: '/admin/communication-mocks',
    loggedUiEvents: '/admin/logged-ui-events',
    users: '/admin/users',
    prices: '/admin/prices',
    products: '/admin/products',
    institutes: '/admin/institutes',
    adminFiltersUser: '/admin/filters/users',
    adminFiltersGroup: '/admin/filters/groups',

    institute: (id: string | number) => `/admin/institutes/${id}`,
    addInstitute: `/admin/institutes/${newString}`,
    user: (id: string | number, tab?: ':tab' | string) => `/admin/users/${id}/${tab || ''}`,
    addUser: '/admin/add-user',
    groups: '/admin/groups',
    group: (id: string | number) => `/admin/groups/${id}`,
    courses: '/admin/courses',
    course: (id: string | number, tab?: ':tab' | string) => `/admin/courses/${id}${tab ? `/${tab}` : ''}`,
    subscriptions: '/admin/subscriptions',
    signUpCodes: '/admin/sign-up-codes/',
    signUpCode: (id: string | number) => `/admin/sign-up-codes/${id}`,
    addSignUpCode: `/admin/sign-up-codes/${newString}`,
    insights: '/admin/insights',
    subscription:  (id: string | number) => `/admin/subscription/${id}`,
    subscriptionRules: '/admin/subscription-rules',
    subscriptionRule: (id: string | number) => `/admin/subscription-rules/${id}`,
    addSubscriptionRule: `/admin/subscription-rules/${newString}`,
    files: '/admin/files',
    file: (id: string | number, tab?: ':tab' | string) => `/admin/files/${id}/${tab || ''}`,
    reagents: '/admin/reagents',
    reagent: (id: string | number, tab?: ':tab' | string) => `/admin/reagents/${id}/${tab || ''}`,
}

const stripeBase = isDev() || isInternal() ? 'https://dashboard.stripe.com/test': 'https://dashboard.stripe.com';
export const externalLinks = {
    privacyPolicy: 'https://app.termly.io/document/privacy-policy/0e531131-f5b9-4760-a418-3d19f8295ed8',
    cookiePolicy: 'https://app.termly.io/document/cookie-policy/cc521b9b-190b-41ca-b343-02b532ae306e',
    termsOfService: 'https://app.termly.io/document/terms-of-service/b20c1c44-2080-45c4-9339-24474c1f7b13',
    eula: 'https://app.termly.io/document/eula/ed99d7bc-7947-4a2e-8048-e13749f792f9',
    photo51desktop: 'photo51://',
    newStripeProduct: `${stripeBase}/products?active=true&create=product&source=product_list`,
    // User should create a new price via a product
    newPrice: `${stripeBase}/products?active=true`,
    stripeSearch(query: string) {
        return `${stripeBase}/search?query=${query}`;
    },
    stripeProduct(productStripeId: string) {
        return `${stripeBase}/products/d/${productStripeId}`;
    }
}
